import apiClient from '@/lib/api-client';
import { ApiError } from '@/model/ApiError';
import { Module, Mutation, VuexModule, Action } from 'vuex-module-decorators';
import {
    Sheet,
    SheetWithItems,
    SheetRequest,
    EmailInvite,
} from '@/model/Sheet';
import { createDateFromUTCString } from '@/lib/date-util';
import { EventFlow } from '@/model/event-flow/EventFlow';
import { setFlow } from '@/lib/helper/event-flow-helper';
import router from '@/router';

@Module({ namespaced: true, preserveState: false })
export default class SheetStorage extends VuexModule {
    error: ApiError | null = null;

    sheets: Sheet[] | null = null;

    projectSheets: Sheet[] = new Array<Sheet>();

    currentSheetWithItems: SheetWithItems | null = null;

    sheetFlow: EventFlow | null = null;

    @Mutation
    setError(error: ApiError | null): void {
        this.error = error;
    }

    @Mutation
    setProjectSheets(projectSheets: Sheet[] | null): void {
        this.projectSheets = projectSheets ?? new Array<Sheet>();
    }

    @Mutation
    setSheets(sheets: Sheet[]): void {
        sheets?.forEach((sheet) => {
            if (sheet.startDate) {
                sheet.startDate = createDateFromUTCString(
                    sheet.startDate.toString(),
                );
            }
        });

        this.sheets = sheets;
    }

    @Mutation
    setCurrentSheetWithItems(sheetWithItems: SheetWithItems): void {
        sheetWithItems.startedAt = createDateFromUTCString(
            sheetWithItems.startedAt.toString(),
        );
        sheetWithItems.endedAt = createDateFromUTCString(
            sheetWithItems.endedAt.toString(),
        );
        let i = 0;
        sheetWithItems.sheetItems.forEach((sheetWithItem) => {
            sheetWithItem.startedAt = createDateFromUTCString(
                sheetWithItem.startedAt.toString(),
            );
            sheetWithItem.endedAt = createDateFromUTCString(
                sheetWithItem.endedAt.toString(),
            );
            sheetWithItem.index = i;
            i += 1;
        });
        this.currentSheetWithItems = sheetWithItems;
        this.sheetFlow = setFlow(sheetWithItems?.sheetItems ?? []);
    }

    @Action
    fetchSheets(): Promise<void | Sheet[]> {
        return apiClient
            .get<void, Sheet[]>('/sheets')
            .then((data: Sheet[]) => {
                this.context.commit('setSheets', data);
                this.context.commit('setError', null);
            })
            .catch((error) => {
                this.context.commit('setError', error);
                return Promise.reject();
            });
    }

    @Action
    fetchProjectSheets(eventId: number): Promise<void | Sheet[]> {
        return apiClient
            .get<void, Sheet[]>(`/events/${eventId}/sheets`)
            .then((data: Sheet[]) => {
                this.context.commit('setProjectSheets', data);
                this.context.commit('setError', null);
            })
            .catch((error) => {
                this.context.commit('setError', error);
                return Promise.reject();
            });
    }

    @Action
    fetchCurrentSheetWithItems(
        sheetToken: string,
    ): Promise<void | SheetWithItems> {
        return apiClient
            .get<void, SheetWithItems>(`/sheets/${sheetToken}`)
            .then((data: SheetWithItems) => {
                this.context.commit('setCurrentSheetWithItems', data);
                this.context.commit('setError', null);
            })
            .catch((error) => {
                if (error?.key === 'not-found.sheet') {
                    router.push({ name: 'NotFound' });
                }

                this.context.commit('setError', error);
                return Promise.reject();
            });
    }

    @Action
    create(request: SheetRequest): Promise<void> {
        return apiClient
            .post<SheetRequest, Sheet>(
                `/events/${request.eventId}/sheets`,
                request.sheet,
            )
            .then(() => {
                this.context.commit('setError', null);
            })
            .catch((error) => {
                this.context.commit('setError', error);
                return Promise.reject();
            });
    }

    @Action
    update(request: SheetRequest): Promise<void> {
        return apiClient
            .put<SheetRequest, Sheet>(
                `/events/${request.eventId}/sheets/${request.sheet.id}`,
                request.sheet,
            )
            .then(() => {
                this.context.commit('setError', null);
            })
            .catch((error) => {
                this.context.commit('setError', error);
                return Promise.reject();
            });
    }

    @Action
    updateName(request: SheetRequest): Promise<void> {
        return apiClient
            .put<SheetRequest, Sheet>(`/sheets/${request.sheet.id}`, {
                data: request.sheet.name,
            })
            .then(() => {
                this.context.commit('setError', null);
            })
            .catch((error) => {
                this.context.commit('setError', error);
                return Promise.reject();
            });
    }

    @Action
    remove(sheetId: number): Promise<void> {
        return apiClient
            .delete(`/sheets/${sheetId}`)
            .then(() => {
                this.context.commit('setError', null);
            })
            .catch((error) => {
                this.context.commit('setError', error);
                return Promise.reject();
            });
    }

    @Action
    invite(emailInvite: EmailInvite): Promise<void> {
        return apiClient
            .post<EmailInvite, void>(`/sheets/${emailInvite.sheetId}/share`, {
                email: emailInvite.email,
            })
            .then(() => {
                this.context.commit('setError', null);
            })
            .catch((error) => {
                this.context.commit('setError', error);
                return Promise.reject();
            });
    }

    get getError(): ApiError | null {
        return this.error;
    }

    get getSheets(): Sheet[] | null {
        return this.sheets;
    }

    get getCurrentSheetWithItems(): SheetWithItems | null {
        return this.currentSheetWithItems;
    }

    get getSheetFlow(): EventFlow | null {
        return this.sheetFlow;
    }

    get getProjectSheets(): Sheet[] {
        return this.projectSheets;
    }
}
